import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, FormArray, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RichTextEditorComponent } from '../../../../../shared/rich-textEditor/rich-textEditor.component';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import {
  MatCheckboxModule,
  MatCheckboxChange,
} from '@angular/material/checkbox';
import { GetFileIconUtilityService } from '@/app/services/utility/get-file-icon-utility.service';
import { SelectProjectDesignComponent } from '@/app/components/projects/shared/tendering/ITT/answer-selected-questions/select-project-design/select-project-design.component';
import { FileManagerComponent } from "@/app/components/shared/file-manager/file-manager.component";
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-answer-selected-questions',
  standalone: true,
  imports: [
    RichTextEditorComponent,
    CommonModule,
    MatIconModule,
    MatCheckboxModule,
    FileManagerComponent,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule
  ],
  templateUrl: './answer-selected-questions.component.html',
  styleUrl: './answer-selected-questions.component.css',
})
export class AnswerSelectedQuestionsComponent {
  isAddendum: boolean = false;
  resetRichTextEditor = false;
  selectedQueries: any;
  queryAnswer = '';
  selectedFiles: any[] = []; // Store selected files
  selectedProjectDesignFiles: any;
  progressPercentage = 60; // Percentage to fill the progress bar

  form = new FormGroup({
    titleControl: new FormControl('', Validators.required),
    rfpQueryResponseControl: new FormControl('', Validators.required),
    newClarificationFiles: new FormArray([]),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly dialogData: any,
    private answerDialogRef: MatDialogRef<AnswerSelectedQuestionsComponent>,
    private tenderingService: TenderingService,
    private getFileIconService: GetFileIconUtilityService,
    private dialog: MatDialog,
     private sanitizer: DomSanitizer
  ) { 
    this.selectedQueries = this.dialogData.selectedQueries;
  } 
  toggleAddendum(event: MatCheckboxChange) {
    if (event.checked) {
      this.isAddendum = true;
      return;
    }
    this.isAddendum = false;
  }

  updateQueryAnswer(content: string) {
    this.form.get('rfpQueryResponseControl')?.setValue(content);
  }

  close(success: boolean = false) {
    this.answerDialogRef.close({ success: success });
  }

  // Get the file icon from the service
  getFileIcon(filePath): string {
    return this.getFileIconService.getFileIconByFilePath(filePath);
  }

  openRFPDesignUploadDialog(): void {
    const designDialogueRef = this.dialog.open(SelectProjectDesignComponent, {
      width: '80vw',
      // height: '70vh',
      panelClass: 'custom-dialog-container',
      maxWidth: '80vw',
      autoFocus: false, // Prevent automatic focus stealing
      disableClose: true, // Prevent closing on outside click
      data: {
        projectId: this.dialogData.projectId,
        selectedFiles: this.selectedProjectDesignFiles
      },
    });

    designDialogueRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        this.selectedProjectDesignFiles = result?.selectedFiles;
        this.mapSelectedFiles(result?.selectedFiles);
      } else {
        console.warn('No valid approval found in the response:', result);
      }
    });
  }

  mapSelectedFiles(result: any): void {
    const categories = [
      'drawing',
      'specifications',
      'calculations',
      'surverys_reports_studies',
      'design_review_reports',
      'permits_and_applications',
      'design_signoff',
      'rfi',
      'addendums',
    ];

    this.selectedFiles = categories.flatMap(
      (category) =>
        result[category]?.map((file: any) => ({
          id: file.id,
          name: file.title,
          filePath: file.file_path,
          type: file.type,
        })) || []
    );
  }

  removeSelection(index: number, file: any): void {
    console.log(file);
    const tabName = file.type;

    if (this.selectedProjectDesignFiles[tabName]) {
      this.selectedProjectDesignFiles[tabName] = this.selectedProjectDesignFiles[tabName].filter(doc => doc.id !== file.id);
    }

    this.selectedFiles.splice(index, 1);
  }

  prepareDataToSubmit() {
    if (!this.form.get('rfpQueryResponseControl')?.valid) {
      console.log('invalid');
      return;
    }

    const dataToSubmit = new FormData();
    dataToSubmit.append('title', this.form.get('titleControl')?.value);
    dataToSubmit.append('query_response', this.form.get('rfpQueryResponseControl')?.value);
    dataToSubmit.append('is_addendum', this.isAddendum.toString());
    dataToSubmit.append('query_ids', JSON.stringify(this.dialogData.selectedQueriesIds));
    dataToSubmit.append('files', JSON.stringify(this.selectedFiles));

    if (this.form.value.newClarificationFiles && Array.isArray(this.form.value.newClarificationFiles)) {
      this.form.value.newClarificationFiles.forEach((uploadedFile: File) => {
        if (uploadedFile) {
          dataToSubmit.append('rfp_clarification_new_files[]', uploadedFile);
        }
      });
    }

    return dataToSubmit;
  }

  /**
   * Handling File Operations - START
   */
  // Uploading/Deleting new files for site preparation
  handleNewFiles(filesData: {
    files: File[],
    controlName: string,
    isEmpty: boolean,
  }) {
    switch (filesData.controlName) {
      case 'newClarificationFiles': {
        const fileArray = this.form.get('newClarificationFiles') as FormArray;
        fileArray.clear();
        // If no files are present, we break
        if (filesData.isEmpty) { break; }
        filesData.files.forEach(file => fileArray.push(new FormControl(file)));
        break;
      }

      default:
        break;
    }
  }
  /**
   * Handling File Operations - END
   */


  /**
   * HTTP REQUESTS START
   */
  submitRFPQueryResponse() {
    const data = this.prepareDataToSubmit();

    return this.tenderingService
      .storeRfpQueriesResponse(
        data,
        this.dialogData.projectId,
        this.dialogData.rfpId
      )
      .subscribe({
        next: (response: any) => {
          if (response.meta.success) {
            this.close(true);
          }
        },
        error: (error) => {
          console.error('Error fetching data:', error);
        },
      });
  }
   sanitizeHtml(html: string): SafeHtml {
      return this.sanitizer.bypassSecurityTrustHtml(html);
    }
  /**
   * HTTP REQUESTS END
   */
}
